import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import VariantDisplay from '../components/VariantDisplay';
import ProductOptionsDisplay from '../components/CustomOptionsDisplay';
import StarRating from '../components/StarRating';
import CustomerRentalCalendar from '../components/rental-calendar/CustomerRentalCalendar';
import { 
  ArrowLeft, Edit, Trash2, Home, DollarSign, MapPin, Calendar,
  ChevronLeft, ChevronRight, User, CheckCircle, Shield, AlertCircle,
  Clock, Package, Users, Coffee, Maximize2, BedDouble, Loader2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { getImageUrl, getFirstImage } from '../utils/imageHelper';
import ListingImage from '../components/ListingImage';

const RentalDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rental, setRental] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [deleting, setDeleting] = React.useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [variantSelections, setVariantSelections] = useState({});
  const [variantPriceAdjustment, setVariantPriceAdjustment] = useState(0);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomTypesLoading, setRoomTypesLoading] = useState(false);
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState(null);

  React.useEffect(() => {
    const fetchRoomTypes = async () => {
      if (!rental?.data?.is_hotel) return;
      setRoomTypesLoading(true);
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = sessionStorage.getItem('token');
        const res = await fetch(`${BACKEND_URL}/api/listings/${id}/room-types`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const types = await res.json();
        // Fetch each room type's base nightly rate in parallel
        const withRates = await Promise.all(types.map(async (rt) => {
          try {
            const rres = await fetch(`${BACKEND_URL}/api/listings/${id}/room-types/${rt.id}/rates`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const rates = rres.ok ? await rres.json() : [];
            const baseRate = rates.find((r) => r.rate_type === 'base');
            return { ...rt, nightly_price: baseRate?.nightly_price || 0 };
          } catch {
            return { ...rt, nightly_price: 0 };
          }
        }));
        setRoomTypes(withRates);
        // Default selection: cheapest active+available priced room type, else first
        const active = withRates.filter((rt) => rt.is_available !== false && rt.nightly_price > 0);
        const pool = active.length > 0 ? active : withRates;
        if (pool.length > 0) {
          const cheapest = pool.reduce((min, rt) => (rt.nightly_price < min.nightly_price ? rt : min), pool[0]);
          setSelectedRoomTypeId(cheapest.id);
        }
      } catch (error) {
        console.error('Error fetching room types:', error);
      } finally {
        setRoomTypesLoading(false);
      }
    };
    fetchRoomTypes();
  }, [rental, id]);
  
  React.useEffect(() => {
    const fetchRental = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = sessionStorage.getItem('token');
        
        const response = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setRental(data);
        } else {
          navigate('/listings/rentals');
        }
      } catch (error) {
        console.error('Error:', error);
        navigate('/listings/rentals');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) fetchRental();
  }, [id, navigate]);
  
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this rental?')) return;
    
    setDeleting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');
      
      const response = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Rental deleted successfully' });
        navigate('/listings/rentals');
      } else {
        toast({ title: 'Error', description: 'Failed to delete rental', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error deleting rental', variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const handleVariantSelectionChange = ({ selections, priceAdjustment }) => {
    setVariantSelections(selections);
    setVariantPriceAdjustment(priceAdjustment || 0);
  };

  const nextImage = () => {
    if (rental?.media?.length > 0) {
      setSelectedImage((prev) => (prev + 1) % rental.media.length);
    }
  };

  const prevImage = () => {
    if (rental?.media?.length > 0) {
      setSelectedImage((prev) => (prev - 1 + rental.media.length) % rental.media.length);
    }
  };

  const getDisplayPrice = () => {
    // v8 fix: canonical pricing-engine output (aniyor_listing_price), not a
    // hardcoded `data.dailyRate` read — handles per_night/per_week/etc and
    // (for Hotels) is overridden below by the selected Room Type's rate.
    const basePrice = parseFloat(rental?.aniyor_listing_price || 0);
    // If custom options have calculated a price, use that as base
    if (calculatedPrice !== null) {
      return calculatedPrice + variantPriceAdjustment;
    }
    return basePrice + variantPriceAdjustment;
  };

  const handlePriceChange = (newPrice) => {
    setCalculatedPrice(newPrice);
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }
  
  if (!rental) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Rental not found</div>
        </div>
      </DashboardLayout>
    );
  }
  
  const data = rental.data || {};
  const images = rental.media || [];
  const isHotel = !!data.is_hotel;
  const basePrice = parseFloat(rental?.aniyor_listing_price || 0);
  const priceUnitSuffix = rental?.aniyor_price_unit || '';
  const selectedRoomType = roomTypes.find((rt) => rt.id === selectedRoomTypeId) || null;
  const displayPrice = isHotel ? (selectedRoomType?.nightly_price || basePrice) : getDisplayPrice();
  const displayUnitSuffix = isHotel ? '/night' : (priceUnitSuffix || '/day');
  
  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header with Back and Actions */}
        <div className="flex items-center justify-between mb-6 px-4">
          <Button variant="ghost" onClick={() => navigate('/listings/rentals')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Rentals
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/listings/rentals/edit/${id}`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              <Trash2 className="h-4 w-4 mr-2" />
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4">
          
          {/* LEFT COLUMN - Rental Gallery */}
          <div className="space-y-4">
            {/* Main Image with Navigation */}
            <div className="relative bg-white rounded-xl overflow-hidden border aspect-square">
              {images.length > 0 ? (
                <>
                  <ListingImage
                    src={images[selectedImage]}
                    alt={rental.title}
                    className="w-full h-full object-contain"
                    showPlaceholder={false}
                  />
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <Home className="h-24 w-24 text-gray-300" />
                </div>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((url, index) => (
                  <button
                    key={url.id || url.name || `${url.title || "url"}-${index}`}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index 
                        ? 'border-blue-600 ring-2 ring-blue-200' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <ListingImage
                      src={url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Tabs for Description, Location, Policies */}
            <Tabs defaultValue="description" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="description">Description</TabsTrigger>
                <TabsTrigger value="location">Location</TabsTrigger>
                <TabsTrigger value="policies">Policies</TabsTrigger>
              </TabsList>
              
              <TabsContent value="description" className="mt-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {rental.description}
                    </p>
                    {data.amenities && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="font-medium mb-2">Amenities</h4>
                        <p className="text-sm text-gray-600">{data.amenities}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="location" className="mt-4">
                <Card>
                  <CardContent className="pt-4 space-y-3">
                    {data.address && (
                      <div className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 text-gray-500 mt-0.5" />
                        <div>
                          <h4 className="font-medium">Address</h4>
                          <p className="text-sm text-gray-600">{data.address}</p>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-4">
                      {data.city && (
                        <div>
                          <p className="text-sm text-gray-500">City</p>
                          <p className="font-medium">{data.city}</p>
                        </div>
                      )}
                      {data.state && (
                        <div>
                          <p className="text-sm text-gray-500">State</p>
                          <p className="font-medium">{data.state}</p>
                        </div>
                      )}
                      {data.pincode && (
                        <div>
                          <p className="text-sm text-gray-500">Pincode</p>
                          <p className="font-medium">{data.pincode}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="policies" className="mt-4">
                <Card>
                  <CardContent className="pt-4 space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">Cancellation Policy</h4>
                      <Badge variant="outline" className="capitalize">{data.cancellationPolicy || 'Flexible'}</Badge>
                    </div>
                    {data.rules && (
                      <div>
                        <h4 className="font-medium mb-2">Rental Rules</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.rules}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* RIGHT COLUMN - Rental Info & Configuration */}
          <div className="space-y-6">
            {/* Title & Status */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={rental.status === 'active' ? 'default' : 'secondary'}>
                  {rental.status}
                </Badge>
                {data.instantBooking && <Badge className="bg-green-600">Instant Booking</Badge>}
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{rental.title}</h1>
              
              {/* Star Rating */}
              <div className="mt-2">
                <StarRating 
                  listingId={id}
                  size="md"
                  showCount={true}
                  fetchData={true}
                />
              </div>
            </div>

            {/* Vendor Info */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <User className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Listed by</p>
                <p className="font-medium">{rental.vendor_name || 'Owner'}</p>
              </div>
            </div>

            {/* Category */}
            <div className="flex gap-2">
              <Badge variant="outline">{rental.category}</Badge>
              {rental.subcategory && <Badge variant="outline">{rental.subcategory}</Badge>}
            </div>

            {/* Pricing Section */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-gray-900" data-testid="rental-details-price">
                  ₹{Number(displayPrice).toLocaleString('en-IN')}
                </span>
                <span className="text-lg text-gray-500">{displayUnitSuffix}</span>
              </div>
              {isHotel && selectedRoomType && (
                <p className="text-sm text-gray-500 mt-1">for the <strong>{selectedRoomType.name}</strong> room type</p>
              )}
              {!isHotel && variantPriceAdjustment > 0 && (
                <p className="text-sm text-green-600 mt-1">
                  +₹{variantPriceAdjustment.toLocaleString()} for selected options
                </p>
              )}

              {/* Additional Rates (non-hotel only) */}
              {!isHotel && (
                <div className="mt-3 pt-3 border-t space-y-2">
                  {data.weeklyRate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Weekly Rate</span>
                      <span className="font-medium">₹{parseFloat(data.weeklyRate).toLocaleString()}</span>
                    </div>
                  )}
                  {data.monthlyRate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Monthly Rate</span>
                      <span className="font-medium">₹{parseFloat(data.monthlyRate).toLocaleString()}</span>
                    </div>
                  )}
                  {data.securityDeposit && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Security Deposit</span>
                      <span className="font-medium">₹{parseFloat(data.securityDeposit).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Room Types — OTA-style selector, Hotel listings only */}
            {isHotel && (
              <div data-testid="room-types-preview-section">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Room Types</h3>
                {roomTypesLoading ? (
                  <div className="flex items-center gap-2 text-gray-500 text-sm py-4">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading room types...
                  </div>
                ) : roomTypes.length === 0 ? (
                  <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 border border-dashed">
                    No room types configured yet. Add room types from the Edit screen.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {roomTypes.map((rt) => {
                      const isSelected = rt.id === selectedRoomTypeId;
                      const img = rt.images?.[0];
                      return (
                        <button
                          type="button"
                          key={rt.id}
                          onClick={() => setSelectedRoomTypeId(rt.id)}
                          data-testid={`room-type-card-${rt.id}`}
                          className={`w-full text-left flex gap-4 p-3 rounded-xl border-2 transition-all ${
                            isSelected ? 'border-blue-600 bg-blue-50/50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className="w-28 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                            {img ? (
                              <img src={getImageUrl(img)} alt={rt.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-300">
                                <BedDouble className="h-8 w-8" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className="font-semibold text-gray-900 truncate">{rt.name}</p>
                              {isSelected && <Badge className="bg-blue-600 flex-shrink-0">Selected</Badge>}
                            </div>
                            {rt.description && (
                              <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{rt.description}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-600">
                              {((rt.max_adults > 0) || (rt.max_children > 0)) && (
                                <span className="flex items-center gap-1">
                                  <Users className="h-3.5 w-3.5" />
                                  {rt.max_adults || 0} Adults{rt.max_children > 0 ? ` + ${rt.max_children} Children` : ''}
                                </span>
                              )}
                              {rt.room_size_sqft > 0 && (
                                <span className="flex items-center gap-1">
                                  <Maximize2 className="h-3.5 w-3.5" /> {rt.room_size_sqft} sqft
                                </span>
                              )}
                              {rt.breakfast_included && (
                                <span className="flex items-center gap-1 text-green-700">
                                  <Coffee className="h-3.5 w-3.5" /> Breakfast included
                                </span>
                              )}
                            </div>
                            {rt.amenities?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {rt.amenities.slice(0, 4).map((a, idx) => (
                                  <Badge key={idx} variant="outline" className="text-[10px] font-normal">{a}</Badge>
                                ))}
                                {rt.amenities.length > 4 && (
                                  <Badge variant="outline" className="text-[10px] font-normal">+{rt.amenities.length - 4} more</Badge>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <p className="text-lg font-bold text-gray-900">₹{Number(rt.nightly_price || 0).toLocaleString('en-IN')}</p>
                            <p className="text-xs text-gray-500">/night</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  Preview mode — this is how room type pricing will appear to customers. Select a room type to see its price above.
                </p>
              </div>
            )}

            {/* Rental Variants Display */}
            {(rental.variants?.length > 0 || rental.data?.variants?.length > 0) && (
              <VariantDisplay 
                variants={rental.variants?.length > 0 ? rental.variants : (rental.data?.variants || [])}
                variantCombinations={rental.variant_combinations?.length > 0 ? rental.variant_combinations : (rental.data?.variant_combinations || rental.data?.variantCombinations || [])}
                title="Rental Options" 
                compact={true}
                basePrice={basePrice}
                onSelectionChange={handleVariantSelectionChange}
              />
            )}

            {/* Custom Options Display - shown for all listing types */}
            {(rental.has_custom_options || rental.data?.hasCustomOptions || rental.data?.customOptionsConfig?.options?.length > 0 || rental.data?.productOptionsConfig?.options?.length > 0) && (
              <ProductOptionsDisplay 
                listingId={id}
                basePrice={basePrice}
                variantSelections={variantSelections}
                variantPriceAdjustment={variantPriceAdjustment}
                onPriceChange={handlePriceChange}
              />
            )}

            {/* Rental Details */}
            <Card>
              <CardContent className="pt-4 space-y-3">
                {(data.minimumRentalPeriod || data.maximumRentalPeriod) && (
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-500">Rental Period: </span>
                      <span className="font-medium">
                        {data.minimumRentalPeriod && `Min ${data.minimumRentalPeriod} days`}
                        {data.minimumRentalPeriod && data.maximumRentalPeriod && ' - '}
                        {data.maximumRentalPeriod && `Max ${data.maximumRentalPeriod} days`}
                      </span>
                    </div>
                  </div>
                )}
                {data.city && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-500">Location: </span>
                      <span className="font-medium">{data.city}{data.state && `, ${data.state}`}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Availability Calendar with Price Preview */}
            <Card data-testid="rental-details-calendar-section">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  Availability &amp; Pricing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CustomerRentalCalendar
                  listingId={id}
                  pricing={{
                    pricingModel:    data.pricingModel,
                    dailyRate:       data.dailyRate,
                    weeklyRate:      data.weeklyRate,
                    monthlyRate:     data.monthlyRate,
                    hourlyRate:      data.hourlyRate,
                    pricePerUnit:    data.pricePerUnit,
                    pricePerEvent:   data.pricePerEvent,
                    securityDeposit: data.securityDeposit,
                  }}
                  minimumDuration={data.minimumRentalDuration || 1}
                  durationUnit={data.rentalDurationUnit || 'day'}
                  slotDurationHours={
                    (data.pricingModel === 'per_hour' || data.pricingModel === 'per_unit' || data.pricingModel === 'per_event')
                    && data.slotDuration
                      ? Number(data.slotDuration) / 60
                      : null
                  }
                  openingTime={data.openingTime || data.availability?.openingTime || '08:00'}
                  closingTime={data.closingTime || data.availability?.closingTime || '20:00'}
                  hotelMode={
                    !!(data.rentalTemplate === 'hotel' ||
                       data.rentalTemplate === 'accommodation' ||
                       data.template === 'hotel' ||
                       (rental?.subcategory_name || rental?.subcategory || '').toLowerCase().includes('hotel') ||
                       (rental?.subcategory_name || rental?.subcategory || '').toLowerCase().includes('ashram') ||
                       (rental?.category_name || rental?.category || '').toLowerCase().includes('yatra'))
                  }
                  currency="₹"
                  onSelectRange={(start, end) => {
                    // Surface to parent for booking flow
                  }}
                />
              </CardContent>
            </Card>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <Button variant="outline" className="w-full h-12 text-lg" disabled>
                <Package className="h-5 w-5 mr-2" />
                BOOK NOW
              </Button>
              <p className="text-xs text-center text-gray-500">
                (Preview mode — select dates above to see pricing)
              </p>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t">
              <div className="text-center">
                <CheckCircle className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Verified</p>
              </div>
              <div className="text-center">
                <Shield className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Secure</p>
              </div>
              <div className="text-center">
                <AlertCircle className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Support</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RentalDetails;